import { createContext, useContext, type RefObject } from 'react'
import type { DateRange } from '../lib/geometry'
import type { ISODate } from '../lib/format'
import type { HeaderTiers } from '../lib/header-tiers'
import type { VisibleRow } from '../lib/tree'
import type { ScheduleActivity } from '../types'

/**
 * Everything a timeline layer needs to draw itself in content coordinates.
 * Lives in its own module so the layers (Task 9/10) can read it without a
 * circular import through `GanttView`, which re-exports all three names.
 */
export interface GanttLayout {
  rows: VisibleRow<ScheduleActivity>[]
  rowIndexById: Map<string, number>
  range: DateRange
  pxPerDay: number
  gridWidth: number
  viewportWidth: number
  viewportHeight: number
  scrollLeft: number
  scrollTop: number
  today: ISODate
  /** Full drawable width of the timeline: at least the timeline viewport. */
  timelineWidth: number
  /** The scrollable pane's DOM node, so a timeline layer can pan it directly (Task 9 §Step 5). */
  scrollerRef: RefObject<HTMLDivElement>
  /** Review fix (Minor #11): computed once here (locale-aware) instead of separately by both
   * `GanttTimeline` (gridlines, `bottom` only, no locale) and `GanttTimelineHeader` (labels, both
   * tiers) — `headerTiers` builds four `Intl.DateTimeFormat`s, so doing it twice on every
   * horizontal scroll step was pure duplicate work. */
  tiers: HeaderTiers
}

export const GanttLayoutContext = createContext<GanttLayout | null>(null)

export const useGanttLayout = (): GanttLayout => {
  const value = useContext(GanttLayoutContext)
  if (!value) throw new Error('useGanttLayout outside GanttLayoutContext')
  return value
}
